import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Componentes que ahora mismo necesitan el scroll de la pagina bloqueado.
 * El bloqueo es uno solo (el `<body>`), asi que se suelta cuando lo suelta el
 * ultimo. Ver `releaseScrollLock`.
 *
 * @type {Set<DialogComponent>}
 */
const scrollLockHolders = new Set();

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    // Red de seguridad del bloqueo de scroll: `close` lo emite el `<dialog>`
    // SIEMPRE, cierre quien cierre — incluido el gesto de atras de Android y
    // el `Escape` nativo, que no pasan por `closeDialog`. Ver `releaseScrollLock`.
    //
    // Va en FASE DE CAPTURA sobre el componente y no sobre `refs.dialog`, por
    // dos motivos: `close` no burbujea (pero la captura si baja hasta el
    // destino, el mismo truco que usa `assets/roro-cart-reward.js` con
    // `dialog:open`), y el `<dialog>` lo sustituye el morphing de la Section
    // Rendering API, asi que un listener atado al nodo se perderia en el
    // primer repintado del carrito.
    this.addEventListener('close', this.#handleNativeClose, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.removeEventListener('close', this.#handleNativeClose, true);

    // Si el componente se va del DOM con el dialogo abierto (una navegacion
    // con vista de transicion, un morphing de seccion), el body se queda
    // bloqueado y no hay quien lo libere. Aqui si.
    this.releaseScrollLock(false);
  }

  #handleNativeClose = () => {
    this.releaseScrollLock();
  };

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Devuelve el `<body>` a la normalidad tras un bloqueo de scroll.
   *
   * POR QUE ES UNA FUNCION APARTE Y NO SOLO EL FINAL DE `closeDialog`
   *
   * `showDialog` deja el body en `position: fixed` para que la pagina de
   * detras no se desplace. Eso solo se deshacia dentro de `closeDialog`, y hay
   * varias formas de cerrar un `<dialog>` que NO pasan por ahi:
   *
   *   · el gesto/boton ATRAS de Android, que Chrome traduce a un cierre nativo
   *     del dialogo;
   *   · `Escape`, que el `<dialog>` gestiona el solo si el foco esta dentro;
   *   · cualquier `dialog.close()` que venga de otro sitio.
   *
   * En todos esos casos el body se quedaba `fixed` y `top: -Npx` para siempre:
   * la pagina entera dejaba de poder desplazarse y ademas saltaba al principio.
   * En movil es el fallo mas grave de la lista, porque el gesto de atras es la
   * forma NORMAL de cerrar un cajon. Detectado el 2026-09-08.
   *
   * El listener `close` de `connectedCallback` llama aqui, asi que el body se
   * libera SIEMPRE, cierre quien cierre. Es idempotente: llamarla dos veces
   * (una por `closeDialog` y otra por el evento) no hace nada la segunda.
   *
   * VARIOS DIALOGOS A LA VEZ (2026-09-10)
   *
   * El bloqueo es uno para toda la pagina, pero lo piden varios componentes y a
   * veces se solapan: el quick add se cierra por `cart:update` justo cuando el
   * cajon del carrito se abre por el mismo evento. Antes cada componente se
   * guardaba su propio `scrollY` al abrir, y el segundo lo leia con el body ya
   * `fixed`, es decir 0: al cerrar el carrito la pagina volvia arriba del todo.
   * Ahora la posicion vive en `body.style.top` (solo la escribe el primero que
   * bloquea, en `#lockScroll`) y `scrollLockHolders` cuenta quien sigue
   * necesitando el bloqueo. Sigue siendo idempotente: si este componente ya lo
   * habia soltado, no hace nada.
   *
   * @param {boolean} [restoreScroll=true] - Si devolver la pagina a donde estaba.
   */
  releaseScrollLock(restoreScroll = true) {
    if (!scrollLockHolders.delete(this)) return;
    if (scrollLockHolders.size > 0) return;
    if (document.body.style.position !== 'fixed') return;

    const top = parseInt(document.body.style.top || '0', 10);

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';

    if (restoreScroll && top < 0) {
      window.scrollTo({ top: Math.abs(top), behavior: 'instant' });
    }
  }

  /**
   * Bloquea el scroll con `body { position: fixed; top: -Ypx }`. Si otro dialogo
   * ya lo tiene bloqueado no se toca: su `top` es la posicion real, y leer
   * `window.scrollY` ahora daria 0. Ver `releaseScrollLock`.
   */
  #lockScroll() {
    if (document.body.style.position !== 'fixed') {
      const scrollY = window.scrollY;

      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
    }

    scrollLockHolders.add(this);
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Opening in the next frame lets the click that triggered it finish bubbling, so
    // #handleClick does not immediately read it as a click outside and close again.
    requestAnimationFrame(() => {
      this.#lockScroll();

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    this.releaseScrollLock();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

/* ==========================================================================
   Cinturon de seguridad del bloqueo de scroll (a nivel de documento)
   --------------------------------------------------------------------------
   `showDialog` deja el `<body>` en `position: fixed`. `closeDialog` lo suelta,
   y el listener de `close` de cada componente lo suelta tambien cuando el
   cierre es NATIVO (gesto de atras de Android, `Escape` del navegador).

   Esto de aqui es la tercera red, para los caminos que no emiten ningun cierre:
   volver a la pagina desde la cache de atras/adelante (bfcache) con el cajon
   abierto, o una navegacion que se lleva el dialogo por delante. Sin ella el
   body se queda `fixed` y la pagina entera deja de poder desplazarse — en
   movil es el fallo mas caro de todos, porque no hay forma de recuperarse sin
   recargar. Detectado el 2026-09-08.

   La condicion es estricta a proposito: solo se suelta si NO queda ningun
   dialogo modal abierto. Con uno abierto el bloqueo es correcto y no se toca.
   ========================================================================== */
function releaseOrphanedScrollLock() {
  if (document.body.style.position !== 'fixed') return;
  if (document.querySelector('dialog:modal')) return;

  const top = parseInt(document.body.style.top || '0', 10);

  scrollLockHolders.clear();
  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';

  if (top < 0) window.scrollTo({ top: Math.abs(top), behavior: 'instant' });
}

window.addEventListener('pageshow', releaseOrphanedScrollLock);
window.addEventListener('popstate', () => setTimeout(releaseOrphanedScrollLock, 300));

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
